import { Component, OnDestroy, OnInit } from '@angular/core';
import { BaseWithBundleComponent } from '@app/shared/components/base/base-with-bundle.component';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { OfficeService, OfficeUriBuilderParams } from '@app/core/services/office.service';
import { BaseList, BaseWithIdList } from '@app/shared/models/list';
import { Office } from '@app/shared/models/office';
import { ClientService } from '@app/core/services/client.service';
import { UserAssignment } from '@app/shared/models/user-assignment';
import { DepartmentDialogComponent } from '@app/modules/settings/components/dialogs/department-dialog.component';
import { ContactTypes } from '@app/shared/models/contact';
import { Bank } from '@app/shared/models/bank';
import * as _ from 'lodash-es';
import { ActionListBtnConfig, ActionListOption } from '@app/shared/components/action-list/action-list.component';
import { Client } from '@app/shared/models/client';

@Component({
    templateUrl: './office-management.component.html',
    styleUrls: ['./office-management.component.scss']
})
export class OfficeManagementComponent extends BaseWithBundleComponent
                                       implements OnInit, OnDestroy {

    public client: Client;
    public userAssignmentList: BaseList<UserAssignment>;
    public officeList: BaseWithIdList<Office>;
    public selectedOffice: Office;
    public actionListOptions: ActionListBtnConfig<Office>[];

    constructor(protected componentServiceBundle: ComponentServiceBundle,
                protected clientService: ClientService,
                protected officeService: OfficeService) {
        super(componentServiceBundle);
    }

    public ngOnInit() {
        super.ngOnInit();

        this.actionListOptions = [{ option: ActionListOption.DELETE, order: 1 }];
        this.client = this.authenticationService.currentUserValue.client;

        this.clientService.getUserAssignments(this.client.id, false)
                          .subscribe(result => {
                this.userAssignmentList = result;
            }, err => {
                this.handleError(err);
            });

        this.officeService.query(this.generateUriBuilderParams(), null, 'offices')
            .subscribe(result => {
                this.officeList = result;

                if (this.officeList.hasItems) {
                    this.selectOffice(this.officeList.items[0]);
                }
            }, err => {
                this.handleError(err);
            });
    }

    public ngOnDestroy() {
        super.ngOnDestroy();
    }

    public selectOffice(office: Office): void {
        this.selectedOffice = null;

        if (office.id) {
            this.officeService.getById(this.generateUriBuilderParams(office))
                .subscribe(result => {
                    this.selectedOffice = result;
                    this.addUserAssigmentFromItsListEntry();
                }, err => this.handleError(err));
        } else {
            setTimeout(() => {
                this.selectedOffice = office;
            });
        }
    }

    protected addUserAssigmentFromItsListEntry(): void {
        if (this.selectedOffice && this.selectedOffice.id) {
            const foundInList = this.officeList.getItem(this.selectedOffice.id);
            this.selectedOffice.member_structure = foundInList.member_structure;
        }
    }

    public createOffice(): void {
        const dialogRef = this.dialog.open(DepartmentDialogComponent, {
            width: '400px',
            data: {
                userAssignments: this.userAssignmentList.items
            }
        });

        dialogRef.afterClosed()
                 .subscribe(dialogResult => {
             this.selectOffice(new Office({
                 address: {
                     contacts: [
                         { name: this.translateService.instant(ContactTypes.EMAIL.trans), value: ''},
                         { name: this.translateService.instant(ContactTypes.FAX.trans), value: ''},
                         { name: this.translateService.instant(ContactTypes.PHONE.trans), value: ''},
                         { name: this.translateService.instant(ContactTypes.MOBILE.trans), value: ''},
                         { name: this.translateService.instant(ContactTypes.URL.trans), value: ''}
                     ]
                 },
                 member_structure: dialogResult.department,
                 bankDetails: [new Bank({ name: '', iban: '', bic: ''})]
             }));
        });
    }

    public deleteOffice(office: Office): void {
        this.officeService.delete(this.generateUriBuilderParams(office))
                          .subscribe(result => {
                this.officeList.removeItem(office);
            }, err => {
                this.handleError(err);
            });
    }

    public updateInList(office: Office): void {
        this.showSimpleConfirmSnackbar(marker('office_management.message.office_saved'));
        this.selectedOffice = office;

        const limitedOfficeInList = this.officeList.getItem(office.id);

        if (limitedOfficeInList) {
            this.officeList.replaceItem(office);
        } else {
            this.officeList.addItem(office);
            this.officeList.items = _.sortBy(this.officeList.items, o => o.officeCode);
        }
    }

    public cancelEdit(): void {
        this.selectedOffice = null;
    }

    protected generateUriBuilderParams(office?: Office): OfficeUriBuilderParams {
        return new OfficeUriBuilderParams(this.client, null, office);
    }
}
